import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { locationGlyph } from '@/scheduling/lib/appointment-glyphs';
import { cn } from '@/lib/utils';
import { useOrgLocations } from '@/scheduling/services/use-org-locations';
import { useAuthStore } from '@/auth/auth-store';
import type { AppointmentFormValues } from '@/types/appointment-form';

/**
 * LocationSelector — chip row of organization locations.
 * Mirrors Flutter create_appointment_widget.dart:634–985 (no-provider state):
 *   - Pulls locations from the org-locations query (Flutter sources from
 *     `userData.organizationLocations`).
 *   - Default-selects the first location on mount when nothing is set
 *     (calendar_screen.dart:2684).
 *   - Icon mapping uses the location's `name` exactly (case-sensitive
 *     "Office" / "Home" / "Virtual"; everything else → MapPin).
 */
export function LocationSelector() {
  const { watch, setValue } = useFormContext<AppointmentFormValues>();
  const selected = watch('location');

  const orgId = useAuthStore((s) => s.user?.organizationId);
  const { data: locations = [] } = useOrgLocations(orgId);

  useEffect(() => {
    if (!selected && locations.length > 0) {
      const first = locations[0];
      setValue('location', { id: first.id, name: first.name }, { shouldValidate: true });
    }
  }, [locations, selected, setValue]);

  if (locations.length === 0) {
    return (
      <div className="booking-section" data-slot="booking-section">
        <label className="booking-section-label">
          Location
        </label>
        <p className="text-sm text-text-hint mt-2">No locations configured.</p>
      </div>
    );
  }

  // When the list fits in the panel width (≤3) we share space with
  // `flex-1` so all tiles render without a horizontal scroll. With 4+
  // locations we fall back to the fixed-width + scroll layout to keep
  // each tile readable.
  const fitsWithoutScroll = locations.length <= 3;

  return (
    <div className="booking-section" data-slot="booking-section">
      <label className="booking-section-label">
        Location
      </label>
      <div
        className={cn(
          'flex gap-2 mt-2',
          fitsWithoutScroll ? '' : 'overflow-x-auto scrollbar-hide pb-1',
        )}
      >
        {locations.map((loc) => {
          const LocationGlyph = locationGlyph(loc.name);
          const isSelected = selected?.id === loc.id;
          return (
            <button
              key={loc.id}
              type="button"
              onClick={() =>
                setValue('location', { id: loc.id, name: loc.name }, { shouldValidate: true })
              }
              aria-pressed={isSelected}
              data-hue={LOCATION_HUE[loc.name] ?? 'jade'}
              className={cn('booking-location-tile', fitsWithoutScroll ? 'flex-1 min-w-0' : 'flex-shrink-0 min-w-30')}
            >
              <LocationGlyph className="size-4 booking-location-icon" aria-hidden />
              <span className="text-sm font-semibold truncate">{LOCATION_LABEL[loc.name] ?? loc.name}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Category hues, display labels and icons: the same maps as the provider card's Book tiles (Practice's). */
const LOCATION_HUE: Record<string, string> = { Office: 'jade', Virtual: 'sky', Home: 'orange', Block: 'purple' };
const LOCATION_LABEL: Record<string, string> = { Office: 'In clinic', Virtual: 'Video', Home: 'Home', Block: 'Block' };
